// Klondike (turn-one / turn-three) and Double Klondike (2 decks, 9 columns, 8 foundations).
// .ai/game-rules.md "Klondike and Double Klondike".
import type { Card, CardKey, GameId, GameState, PileRef } from '../types';
import { isRed, rankLow } from '../cards';
import { card, dropOnFoundation, liftTopOnly, pileCards, standardFoundationFor, topOf, type VariantRules } from './base';

/** Down by 1 in alternating colours. An Ace never goes to a column ('Aa': [false]). */
function buildsOn(c: Card, onto: Card): boolean {
  if (c.rank === 'A' || onto.rank === 'A') return false;
  return isRed(c.suit) !== isRed(onto.suit) && rankLow(onto.rank) === rankLow(c.rank) + 1;
}

function canLift(s: GameState, pile: PileRef, index: number): boolean {
  if (pile.kind === 'tableau') {
    const col = pileCards(s, pile);
    const key = col[index];
    return key !== undefined && s.faceUp[key] === true;
  }
  if (pile.kind === 'waste' || pile.kind === 'foundation') return liftTopOnly(s, pile, index);
  return false;
}

function canDrop(s: GameState, run: readonly CardKey[], target: PileRef): boolean {
  const bottom = run[0];
  if (bottom === undefined) return false;
  if (target.kind === 'foundation') return dropOnFoundation(s, run, target.index);
  if (target.kind !== 'tableau') return false;
  const col = pileCards(s, target);
  const top = topOf(col);
  if (top === undefined) return card(s, bottom).rank === 'K';
  return buildsOn(card(s, bottom), card(s, top));
}

function make(id: GameId, decks: 1 | 2, columns: number[], foundations: number, turn: 1 | 3): VariantRules {
  return {
    id,
    columns,
    foundations,
    cells: 0,
    decks,
    suits: ['c', 's', 'd', 'h'],
    variableSuits: 0,
    turn,
    hasWaste: true,
    recycle: true,
    allFaceUp: false,
    autoMove: true,
    foundationToTableau: true,
    canLift,
    canDrop,
    foundationFor: standardFoundationFor,
    buildsOn,
    completedRun: () => false,
  };
}

export const klondikeTurnOne = make('klondike:turn-one', 1, [1, 2, 3, 4, 5, 6, 7], 4, 1);
export const klondikeTurnThree = make('klondike:turn-three', 1, [1, 2, 3, 4, 5, 6, 7], 4, 3);
export const doubleKlondikeTurnOne = make('double-klondike:turn-one', 2, [1, 2, 3, 4, 5, 6, 7, 8, 9], 8, 1);
export const doubleKlondikeTurnThree = make('double-klondike:turn-three', 2, [1, 2, 3, 4, 5, 6, 7, 8, 9], 8, 3);
